/**
 * Pre-rotated pixel masks — issue #42, Phase 6.
 *
 * ==== THE LAYER THIS FILE DELETES =======================================
 *
 * Noita's rigid-body pipeline is marching squares → Douglas-Peucker →
 * triangulation → Box2D, with each pixel carrying a UV into the triangle that
 * owns it and the hull recomputed whenever the body is damaged.
 *
 * **The first three steps exist only to translate pixels into polygons for a
 * polygon solver.** Recompute-on-damage is the tax for that translation being
 * lossy: the hull is an approximation, so when the pixels change the
 * approximation has to be rebuilt. If the solver speaks pixels there is no
 * approximation to rebuild and the whole layer — all three steps and the
 * rebuild — is simply absent. Dropping simplification also makes collision
 * *more* accurate, not less: a Douglas-Peucker hull cuts corners off the shape
 * it is describing, and we keep them.
 *
 * What survives the deletion is the one thing marching squares was for:
 * **which pixels are on the edge, and which way the edge faces.** That is a
 * property of a pixel, and it is computed here, once, at bake time — not a
 * build step run against a changing body. `boundary` and `nx`/`ny` below *are*
 * marching squares, kept as a pixel property.
 *
 * ==== 32 ORIENTATIONS, BAKED ============================================
 *
 * A body's pose is a position plus one of 32 orientations (11.25° apart). Each
 * orientation is a flat list of `(dx, dy) → sourcePixel` entries, so placing a
 * body is a linear walk over an `Int16Array`: no trig, no allocation, and no
 * per-cell branch beyond "is this pixel still alive".
 *
 * ==== WHY THE TRIG IS A TABLE OF LITERALS ===============================
 *
 * docs/01 §8 promises the same seed produces the same world on any machine.
 * `Math.sin`/`Math.cos` are **not** specified to be correctly rounded — two
 * engines may differ in the last ulp — and a 1-ulp difference is enough to move
 * one destination cell across a rounding boundary and change a mask, which
 * changes a collision, which changes the run. So the 32 cosines and sines are
 * written below as integers scaled by 2^14, and every rotation is exact integer
 * arithmetic. `Math.sqrt` **is** correctly rounded by IEEE 754 and is used
 * freely; `Math.round` on an exact integer is a no-op. There is no other
 * floating-point operation in the bake.
 *
 * ==== WHY SHAPES MUST HAVE ODD DIMENSIONS ===============================
 *
 * The centre of rotation has to be a lattice point, or the quarter-turns are
 * not bijections. A 12-wide shape centres on 5.5; inverse-rotating destination
 * cell −6 by 90° lands on source 11.5, which rounds *outside* the source grid
 * and drops a column. With odd dimensions the centre is a real cell, the four
 * quarter-turns are exact permutations of the source, and
 * `tests/rigid.test.ts` pins that against a deliberately asymmetric shape.
 */

import { hash2 } from '../core/rng';
import { VOID, tryMatId, type MatId } from './materials';
import { parseShapeMask, specFor, type ShapeMaskDoc } from './shapeMask';

/**
 * The shapes the game ships, **as authored `.pxm` files** — see `shapeMask.ts`.
 *
 * `import.meta.glob` is `?raw` over a directory: Vite expands the pattern at
 * build time and inlines every match as a string literal, so this still costs no
 * fetch, no asset directory and no new dependency. It replaced one static `?raw`
 * per file so that a mask `pixel mask new` writes is a shape the game has,
 * rather than a file nothing imports until somebody edits this line. The bundle
 * cost is the honest difference: `?raw` inlines one file, a glob inlines the
 * directory — 2.3 KB across the two shipped masks.
 *
 * **Wire order is pinned separately, below.** That is the one thing a directory
 * cannot supply and it is the whole reason this is not simply a sorted list.
 */
const SHAPE_TEXT: Record<string, string> = import.meta.glob('./shapes/*.pxm', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * A mask's source text by its repo-relative path (`src/sim/shapes/x.pxm`) —
 * `rooms.ts`'s `tileTextByPath`, one registry over, and for the same consumer:
 * the in-game editor has no filesystem to read the document from.
 */
export function shapeTextByPath(rel: string): string | undefined {
  return SHAPE_TEXT[rel.replace(/^src\/sim\//, './')];
}

/** Orientations per full turn. */
export const ORIENTS = 32;

/** Fixed-point scale for the trig table. 2^14 keeps every product under 2^31. */
const TRIG_SCALE = 16384;

/**
 * `round(cos(2πk/32) · 16384)`, k = 0..31. Literals, not `Math.cos` — see the
 * determinism note above. The quarter-turn entries are exactly 0 and ±16384,
 * which is what makes k ∈ {0, 8, 16, 24} exact permutations.
 */
const COS: readonly number[] = [
  16384, 16069, 15137, 13623, 11585, 9102, 6270, 3196,
  0, -3196, -6270, -9102, -11585, -13623, -15137, -16069,
  -16384, -16069, -15137, -13623, -11585, -9102, -6270, -3196,
  0, 3196, 6270, 9102, 11585, 13623, 15137, 16069,
];

/** `round(sin(2πk/32) · 16384)`, k = 0..31. */
const SIN: readonly number[] = [
  0, 3196, 6270, 9102, 11585, 13623, 15137, 16069,
  16384, 16069, 15137, 13623, 11585, 9102, 6270, 3196,
  0, -3196, -6270, -9102, -11585, -13623, -15137, -16069,
  -16384, -16069, -15137, -13623, -11585, -9102, -6270, -3196,
];

/**
 * Ceiling on pixels in one shape, in every orientation.
 *
 * Not a tuning knob: it is the term the work quota in `rigid.ts` is derived
 * from, and `bakeShape` throws rather than silently exceeding it. 289 is a
 * 17×17 source, which is the largest shape whose rotated bounding box still
 * fits inside the `MAX_EXTENT` below.
 */
export const MAX_SHAPE_CELLS = 289;

/**
 * Ceiling on `|dx|` and `|dy|` in any baked mask, so a body's footprint is
 * bounded before any of it is placed. 12 gives a 25×25 rotated envelope.
 */
export const MAX_EXTENT = 12;

/**
 * Ceiling on boundary pixels in one orientation.
 *
 * The second term of the quota. A 17×17 square has a 64-cell perimeter and its
 * 45° rotation has 92; 128 covers both with room, and the bake asserts it.
 */
export const MAX_BOUNDARY_CELLS = 128;

/**
 * Ceiling on cells in one direction's leading-edge list.
 *
 * A cell can block movement in direction `d` only if the body does not already
 * occupy the cell one step along `d`. That is exact for any mask — convex or
 * not, holed or not — and it is much smaller than the boundary: a 13×13 hollow
 * crate has 64 boundary cells and about 30 in each leading-edge list, because
 * the inner face of the far wall counts too. 96 covers the worst orientation of
 * the largest permitted shape, and the bake asserts it.
 */
export const MAX_EDGE_CELLS = 96;

/** A shape as authored: a rectangle of material ids, `VOID` where empty. */
export interface ShapeSpec {
  name: string;
  /** Odd. See the note above — an even dimension has no centre cell. */
  w: number;
  /** Odd. */
  h: number;
  /** Row-major, length `w · h`. */
  cells: readonly MatId[];
}

/** One of the 32 baked orientations. */
export interface RotatedMask {
  /** Cell offsets from the body's origin cell. Parallel to `dy` and `src`. */
  readonly dx: Int16Array;
  readonly dy: Int16Array;
  /** Which canonical pixel supplies this cell's material and temperature. */
  readonly src: Int32Array;
  /**
   * 1 for the first entry that draws on a given source pixel, 0 for the rest.
   *
   * Inverse mapping is many-to-one at some angles: the crate at 45° covers 75
   * destination cells drawn from 55 source pixels, because a one-cell wall
   * thickens where the sampling stretches. Placing draws **every** entry, so
   * the wall has no holes; absorbing on lift reads only the primary.
   *
   * Two things go wrong without it, both small and both real: a pixel whose
   * primary cell was destroyed while a duplicate's survived leaves that
   * duplicate behind as orphaned wood the body no longer owns, and a burning
   * pixel with two cells is counted twice in the body's burning tally, which
   * is what decides whether it may sleep.
   */
  readonly primary: Uint8Array;
  /** Indices into `dx`/`dy`/`src` of cells with an exposed face. */
  readonly boundary: Int32Array;
  /**
   * Outward normal of each boundary cell, ×100, parallel to `boundary`.
   *
   * This is the marching-squares output, kept per pixel. It is what a contact
   * resolves against, so a crate landing on a slope slides down it rather than
   * stopping dead on the first blocked cell.
   */
  readonly nx: Int8Array;
  readonly ny: Int8Array;
  /**
   * Leading-edge lists, indexed `[right, left, down, up]`. Entry indices whose
   * neighbour one step along that direction is *not* part of the mask — the
   * exact set a move in that direction has to probe. See `MAX_EDGE_CELLS`.
   */
  readonly edge: readonly Int32Array[];
  /** Tight AABB of the rotated mask, in cell offsets from the origin. */
  readonly minDx: number;
  readonly maxDx: number;
  readonly minDy: number;
  readonly maxDy: number;
}

export interface BakedShape {
  readonly name: string;
  /** Canonical pixel count — the body's mass, and the denominator of failure. */
  readonly pixelCount: number;
  /** Canonical material per pixel, in the unrotated pose. */
  readonly mat: Uint16Array;
  /** Unrotated offsets, for authoring and for the exactness tests. */
  readonly baseDx: Int16Array;
  readonly baseDy: Int16Array;
  /**
   * Render grain per canonical pixel, 1..255. Never 0 — see `GRAIN_ABSENT`.
   *
   * **This is the #42 grain-crawl fix, and it is one line of arithmetic in the
   * right place.** `render/terrain.ts` speckles a cell by hashing its *world*
   * coordinate, which is correct for terrain and wrong for a body: a crate's
   * wood grain became a property of where the crate was rather than of the
   * crate, so rotating one made the speckle swim. Keying the byte on
   * `baseDx`/`baseDy` — the pixel's offset in the **unrotated** pose — makes it
   * a property of the pixel, and a pixel keeps its identity through all 32
   * orientations by construction. There is nothing for the renderer to get
   * right beyond looking it up.
   *
   * `sim/rigid.ts`'s `place()` copies these into the body's grain window as it
   * writes cells, and `render/terrain.ts` uploads that window and prefers it to
   * the world hash. The seed is arbitrary and only has to differ from the
   * terrain hash's, so that a crate resting against a wall does not share its
   * speckle.
   */
  readonly grain: Uint8Array;
  /** Half-extent of the rotated envelope, in cells. */
  readonly extent: number;
  readonly masks: readonly RotatedMask[];
}

/**
 * The value a grain window holds where **no body pixel lives**.
 *
 * Zero is spent on "absent" rather than on a grain level because the renderer
 * needs one lookup to answer both "is this cell a body's?" and "what is its
 * grain?", and a body window is mostly empty — a crate at 45° fills about a
 * third of its envelope. So the 255 usable levels start at 1.
 */
export const GRAIN_ABSENT = 0;

/** Grain byte for a canonical pixel at unrotated offset `(bx, by)`. 1..255. */
function grainFor(bx: number, by: number): number {
  return 1 + (hash2(bx, by, 0x9e37) % 255);
}

/** Inverse-rotate a destination offset by orientation `k`, exactly. */
function invRotate(dx: number, dy: number, k: number, out: { u: number; v: number }): void {
  const c = COS[k];
  const s = SIN[k];
  // (u, v) = R(−θ)·(dx, dy). `+ TRIG_SCALE/2` then floor is round-half-up on
  // integers, which is exact: the numerator is an integer well under 2^31 and
  // dividing by a power of two is exact in a double.
  out.u = Math.floor((dx * c + dy * s + TRIG_SCALE / 2) / TRIG_SCALE);
  out.v = Math.floor((-dx * s + dy * c + TRIG_SCALE / 2) / TRIG_SCALE);
}

/**
 * Bake one shape into 32 masks.
 *
 * **Inverse mapping, not forward.** Rotating each source pixel forward and
 * writing where it lands leaves holes — two neighbouring source cells can round
 * to the same destination, and the cell between them is never written. Walking
 * the *destination* envelope and asking each cell where it came from cannot
 * leave a hole by construction, and it is the reason a rotated crate is still a
 * solid crate instead of a sieve. It costs the same and is done once.
 */
export function bakeShape(spec: ShapeSpec): BakedShape {
  const { w, h, cells, name } = spec;
  if (w % 2 !== 1 || h % 2 !== 1) {
    throw new Error(`shape ${name}: ${w}x${h} — both dimensions must be odd`);
  }
  if (cells.length !== w * h) {
    throw new Error(`shape ${name}: ${cells.length} cells for a ${w}x${h} grid`);
  }
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;

  // ---- canonical pixels, in row-major source order so the numbering is a
  // property of the authored grid rather than of the bake's traversal.
  const mats: number[] = [];
  const baseDx: number[] = [];
  const baseDy: number[] = [];
  /** source index -> canonical pixel index, or -1. */
  const pixelAt = new Int32Array(w * h).fill(-1);
  for (let sy = 0; sy < h; sy++) {
    for (let sx = 0; sx < w; sx++) {
      const m = cells[sy * w + sx];
      if (m === VOID) continue;
      pixelAt[sy * w + sx] = mats.length;
      mats.push(m);
      baseDx.push(sx - cx);
      baseDy.push(sy - cy);
    }
  }
  if (mats.length === 0) throw new Error(`shape ${name}: no cells`);
  if (mats.length > MAX_SHAPE_CELLS) {
    throw new Error(`shape ${name}: ${mats.length} cells, cap is ${MAX_SHAPE_CELLS}`);
  }

  // Envelope: the half-diagonal, rounded up. `Math.sqrt` is correctly rounded
  // by IEEE 754, so this is machine-independent.
  const extent = Math.ceil(Math.sqrt(cx * cx + cy * cy));
  if (extent > MAX_EXTENT) {
    throw new Error(`shape ${name}: envelope ${extent}, cap is ${MAX_EXTENT}`);
  }

  const span = 2 * extent + 1;
  const occ = new Uint8Array(span * span);
  const srcOf = new Int32Array(span * span);
  const masks: RotatedMask[] = [];
  const uv = { u: 0, v: 0 };

  for (let k = 0; k < ORIENTS; k++) {
    occ.fill(0);
    const dxs: number[] = [];
    const dys: number[] = [];
    const srcs: number[] = [];

    for (let dy = -extent; dy <= extent; dy++) {
      for (let dx = -extent; dx <= extent; dx++) {
        invRotate(dx, dy, k, uv);
        const sx = uv.u + cx;
        const sy = uv.v + cy;
        if (sx < 0 || sx >= w || sy < 0 || sy >= h) continue;
        const p = pixelAt[sy * w + sx];
        if (p < 0) continue;
        const e = dxs.length;
        dxs.push(dx);
        dys.push(dy);
        srcs.push(p);
        const oi = (dy + extent) * span + (dx + extent);
        occ[oi] = 1;
        srcOf[oi] = e;
      }
    }

    if (dxs.length > MAX_SHAPE_CELLS) {
      throw new Error(
        `shape ${name} at orientation ${k}: ${dxs.length} cells, cap is ${MAX_SHAPE_CELLS}`,
      );
    }

    // ---- boundary + normals. This is the marching-squares result.
    const boundary: number[] = [];
    const nxs: number[] = [];
    const nys: number[] = [];
    for (let i = 0; i < dxs.length; i++) {
      const dx = dxs[i];
      const dy = dys[i];
      let exposed = false;
      let ax = 0;
      let ay = 0;
      // The orthogonal contribution on its own, kept as a tie-break. At 45° a
      // staircase cell can have one open face below and two open diagonals
      // above, whose weighted vectors cancel exactly and leave a zero normal
      // for a cell that plainly faces downward. Falling back to the faces
      // alone answers that correctly.
      let ox4 = 0;
      let oy4 = 0;
      for (let o = 0; o < NEIGHBOURS.length; o += 2) {
        const ox = NEIGHBOURS[o];
        const oy = NEIGHBOURS[o + 1];
        const jx = dx + ox;
        const jy = dy + oy;
        const inside =
          jx >= -extent && jx <= extent && jy >= -extent && jy <= extent &&
          occ[(jy + extent) * span + (jx + extent)] === 1;
        if (inside) continue;
        // Only the four orthogonal faces make a cell a boundary cell; the
        // diagonals contribute to the normal but a cell touched only at a
        // corner is not exposed, and counting it doubles the perimeter.
        if (o < 8) {
          exposed = true;
          ox4 += ox;
          oy4 += oy;
        }
        // Diagonals weigh less, so a flat edge's normal stays axis-aligned
        // instead of being dragged by the two corners at its ends.
        const wgt = o < 8 ? 2 : 1;
        ax += ox * wgt;
        ay += oy * wgt;
      }
      if (!exposed) continue;
      if (ax === 0 && ay === 0) {
        ax = ox4;
        ay = oy4;
      }
      const len = Math.sqrt(ax * ax + ay * ay);
      boundary.push(i);
      if (len === 0) {
        nxs.push(0);
        nys.push(0);
      } else {
        nxs.push(Math.round((ax / len) * 100));
        nys.push(Math.round((ay / len) * 100));
      }
    }

    if (boundary.length > MAX_BOUNDARY_CELLS) {
      throw new Error(
        `shape ${name} at orientation ${k}: ${boundary.length} boundary cells, ` +
          `cap is ${MAX_BOUNDARY_CELLS}`,
      );
    }

    // ---- leading edges, one list per direction, and the tight AABB.
    const edge: Int32Array[] = [];
    for (let d = 0; d < 4; d++) {
      const ox = EDGE_DIRS[d * 2];
      const oy = EDGE_DIRS[d * 2 + 1];
      const list: number[] = [];
      for (let i = 0; i < dxs.length; i++) {
        const jx = dxs[i] + ox;
        const jy = dys[i] + oy;
        const inside =
          jx >= -extent && jx <= extent && jy >= -extent && jy <= extent &&
          occ[(jy + extent) * span + (jx + extent)] === 1;
        if (!inside) list.push(i);
      }
      if (list.length > MAX_EDGE_CELLS) {
        throw new Error(
          `shape ${name} at orientation ${k}, direction ${d}: ${list.length} ` +
            `leading cells, cap is ${MAX_EDGE_CELLS}`,
        );
      }
      edge.push(Int32Array.from(list));
    }

    let minDx = dxs[0];
    let maxDx = dxs[0];
    let minDy = dys[0];
    let maxDy = dys[0];
    for (let i = 1; i < dxs.length; i++) {
      if (dxs[i] < minDx) minDx = dxs[i];
      if (dxs[i] > maxDx) maxDx = dxs[i];
      if (dys[i] < minDy) minDy = dys[i];
      if (dys[i] > maxDy) maxDy = dys[i];
    }

    const primary = new Uint8Array(srcs.length);
    const seen = new Uint8Array(mats.length);
    for (let i = 0; i < srcs.length; i++) {
      if (seen[srcs[i]] === 1) continue;
      seen[srcs[i]] = 1;
      primary[i] = 1;
    }

    masks.push({
      dx: Int16Array.from(dxs),
      dy: Int16Array.from(dys),
      src: Int32Array.from(srcs),
      primary,
      boundary: Int32Array.from(boundary),
      nx: Int8Array.from(nxs),
      ny: Int8Array.from(nys),
      edge,
      minDx,
      maxDx,
      minDy,
      maxDy,
    });
  }

  return {
    name,
    pixelCount: mats.length,
    mat: Uint16Array.from(mats),
    baseDx: Int16Array.from(baseDx),
    baseDy: Int16Array.from(baseDy),
    grain: Uint8Array.from(baseDx, (bx, p) => grainFor(bx, baseDy[p])),
    extent,
    masks,
  };
}

/** Orthogonals first (indices 0..7), then diagonals. The order is load-bearing. */
const NEIGHBOURS = [0, -1, 1, 0, 0, 1, -1, 0, -1, -1, 1, -1, 1, 1, -1, 1];

/** `[right, left, down, up]`, matching the `DIR_*` constants in `rigid.ts`. */
const EDGE_DIRS = [1, 0, -1, 0, 0, 1, 0, -1];

// ------------------------------------------------------------------ content

/**
 * The crate and the hatch used to be two functions here, each building its grid
 * with a predicate over `(x, y)`: `edge || y === 6`, and `fill(wood)`. That
 * worked because a hollow box and a solid plate are the two shapes a `for` loop
 * can express, and issue #115 names the moment it stops working — "pixel masks
 * with boundary normals in 32 pre-rotations, which will not survive being
 * hand-written in source past the first crate". The third shape is a minecart.
 *
 * **Wire order, and why a directory cannot be trusted to supply it.**
 *
 * The index into `shapes()` is the shape id stored on every rigid body —
 * `shapes()[b.shape]`, in `rigid.ts`, sixteen times — so this order may be
 * appended to and **must never be reordered**. Sorting the directory by name
 * would be an ordering, and a stable one, and still wrong: the day somebody
 * draws `barrel.pxm` it sorts before `crate.pxm` and every crate in flight
 * becomes a door.
 *
 * So the shipped ids are pinned here by name, and anything the glob turns up
 * that is not pinned is **appended** in sorted order, which is exactly the
 * append the comment above has always permitted. A new mask therefore loads
 * without an edit here and cannot take an id that is already spoken for.
 *
 * `SHAPE_CRATE` and `SHAPE_DOOR` below are the first two entries and
 * `tests/shapeMask.test.ts` pins the correspondence.
 */
export const SHAPE_WIRE_ORDER: readonly string[] = ['crate.pxm', 'door.pxm'];

/**
 * The order a set of mask files takes: pinned names first, then the rest sorted.
 *
 * **Its own exported function rather than four lines inside the initialiser, and
 * that is a mutation-testing result rather than tidiness** — the same finding
 * `orientOfRoomUnit` records one file over. Inlined, every branch here is
 * unreachable while the two shipped masks happen to sort into the order they are
 * pinned in: deleting the pin, dropping the sort on the tail, and tolerating a
 * missing pinned file were all invisible to every test in the suite. They are
 * driven directly now, with names this repo does not have.
 *
 * `bases` is a set of file names; the result is those names, ordered.
 */
export function orderShapeFiles(wireOrder: readonly string[], bases: Iterable<string>): string[] {
  const have = new Set(bases);
  const pinned = new Set(wireOrder);
  const order: string[] = [];
  for (const base of wireOrder) {
    if (!have.has(base)) {
      throw new Error(
        `rigidShapes: ${base} holds wire id ${order.length} and is not in src/sim/shapes/ — ` +
          'a pinned shape may not be deleted, because every rigid body carrying that id would ' +
          'silently become the next shape along',
      );
    }
    order.push(base);
  }
  for (const base of [...have].sort()) if (!pinned.has(base)) order.push(base);
  return order;
}

const SHAPE_SOURCES: readonly (readonly [string, string])[] = (() => {
  // A `Map`, not an object index: a file called `constructor.pxm` must not
  // resolve to something off `Object.prototype`. `docs/25` §5.2 records four
  // live instances of that shape.
  const byBase = new Map<string, string>();
  for (const key of Object.keys(SHAPE_TEXT)) byBase.set(key.slice(key.lastIndexOf('/') + 1), SHAPE_TEXT[key]);
  return orderShapeFiles(SHAPE_WIRE_ORDER, byBase.keys())
    .map((base) => [`src/sim/shapes/${base}`, byBase.get(base) as string] as const);
})();

/**
 * The authored masks the build holds, repo-relative, in wire order.
 *
 * Exported so a tool can offer the list — `pixel mask edit`'s picker — without
 * scanning a directory and offering a file the build has not got.
 */
export const SHAPE_FILES: readonly string[] = SHAPE_SOURCES.map(([path]) => path);

/**
 * The parsed documents, exposed so a tool can render, edit and re-serialise the
 * exact masks the game bakes rather than its own copy of them.
 *
 * Parsed lazily and cached, for the same reason `shapes()` bakes lazily: a
 * module-level parse is work every importer pays whether or not it has a rigid
 * body, and this module is imported by the world generator.
 */
let cachedDocs: ShapeMaskDoc[] | null = null;

export function shapeDocs(): readonly ShapeMaskDoc[] {
  if (cachedDocs === null) {
    cachedDocs = SHAPE_SOURCES.map(([where, text]) => parseShapeMask(text, where));
  }
  return cachedDocs;
}

/**
 * Every shape the game ships, baked once.
 *
 * Built lazily because material ids are assigned by `registerMaterials()` and a
 * module-level bake would capture whatever `MAT.WOOD` was at import time — in a
 * test file that imports before the harness registers, that is `undefined`.
 * The documents carry material *names* for exactly this reason.
 */
let cachedShapes: BakedShape[] | null = null;

export const SHAPE_CRATE = 0;
export const SHAPE_DOOR = 1;

export function shapes(): readonly BakedShape[] {
  if (cachedShapes === null) {
    if (tryMatId('WOOD') === undefined) {
      throw new Error('rigid shapes baked before registerMaterials()');
    }
    cachedShapes = shapeDocs().map((doc) => bakeShape(specFor(doc, tryMatId)));
  }
  return cachedShapes;
}

/** Test seam: forget the bake so a suite can re-register materials. */
export function resetShapes(): void {
  cachedShapes = null;
}
